<template>
  <div class="svga-player" ref="svgaPlayerRef"></div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import SVGA from 'svgaplayerweb'
import { info, defaultProps } from './config'
import { computeRatio } from '@/packages/plugins/utils'

defineOptions({ name: info.name })

const props = defineProps(defaultProps)
const svgaPlayerRef = ref(null)
let player = null
let parser = null
let displayObserverCleanup = null
let isHiddenByDisplay = false

const isDisplayNone = (el) => {
  if (!el) return false
  return window.getComputedStyle(el).display === 'none'
}

const syncPlaybackByDisplay = () => {
  if (!player) return
  const hidden = isDisplayNone(svgaPlayerRef.value)
  console.log('svg-hidden----', hidden)
  if (hidden === isHiddenByDisplay) return
  isHiddenByDisplay = hidden
  if (hidden) {
    player.pauseAnimation()
  } else {
    player.startAnimation()
  }
}

const setupDisplayObserver = () => {
  const el = svgaPlayerRef.value
  if (!el) return null

  const check = () => syncPlaybackByDisplay()
  const observers = []
  let node = el
  while (node) {
    const observer = new MutationObserver(check)
    observer.observe(node, { attributes: true, attributeFilter: ['style', 'class'] })
    observers.push(observer)
    node = node.parentElement
  }
  check()

  return () => {
    observers.forEach((observer) => observer.disconnect())
  }
}

const updatePlayerContentMode = (value) => {
  if (player && value) {
    player.setContentMode(value)
  }
}

// 加载 SVGA 文件并开始动画
const loadSvga = async () => {
  nextTick(() => {
    if (svgaPlayerRef.value && props.src) {
      const svgaPlayerDom = svgaPlayerRef.value
      // 设置 canvas 的宽高以匹配父容器
      svgaPlayerDom.width = computeRatio(svgaPlayerDom.parentElement.clientWidth)
      svgaPlayerDom.height = computeRatio(svgaPlayerDom.parentElement.clientHeight)

      parser = new SVGA.Parser(svgaPlayerDom)
      player = new SVGA.Player(svgaPlayerDom)
      isHiddenByDisplay = isDisplayNone(svgaPlayerDom)

      // 加载动画并开始播放
      try {
        parser.load(props.src, (videoItem) => {
          player.setVideoItem(videoItem)
          updatePlayerContentMode(props.contentMode)
          if (!isHiddenByDisplay) {
            player.startAnimation()
          }
        })
      } catch (error) {
        console.error('加载 SVGA 文件失败:', error)
      }
    }
  })
}

// 监听 src 变化并重新加载 SVGA 动画
watch(
  () => props.src,
  async (newSrc, oldSrc) => {
    // console.log(newSrc, oldSrc)
    if (newSrc !== oldSrc) {
      player && player.clear()
      await loadSvga()
    }
  }
)

watch(
  () => props.contentMode,
  (newValue) => {
    if (newValue) {
      updatePlayerContentMode(newValue)
    }
  }
)

// 在组件挂载后加载动画
onMounted(async () => {
  await loadSvga()
  displayObserverCleanup = setupDisplayObserver()
})

// 组件卸载时清理 player 实例
onUnmounted(() => {
  displayObserverCleanup?.()
  displayObserverCleanup = null
  if (player) {
    player.clear()
    player = null
  }
})
</script>

<style lang="scss" scoped>
.svga-player {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}
</style>
